/**
 * About component - Section principale du portfolio
 * Affiche les informations person        // Charger le drapeau selon la langue
        try {
          const flagName = language === 'en' ? 'uk-flag' : 'fr-flag';
          const flagResponse = await portfolioApi.assets.getByName(flagName, 'flags');
          if (flagResponse.success && flagResponse.data?.url) {
            setFlagImageUrl(flagResponse.data.url);
          } else {
            setFlagImageUrl('');
          }
        } catch (error) {
          setFlagImageUrl('');
        }hie et liens sociaux
 * Intégration complète avec portfolioApi pour contenu dynamique et images depuis Supabase
 * ✅ VERSION CORRIGÉE - Résolution de la boucle infinie d'images
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import * as styles from './about.css';
import { commonClasses } from "../../../../styles/indexStyles";
import { useLanguage } from "../../../../hooks";
import { useInView } from 'react-intersection-observer';
import { apiService } from "../../../shared/services/apiService";
import { normalizeImageUrl as normalizeImageUrlShared } from "../../../shared/utils/imageUrl";
import { Spinner } from "../../../admin/components/Spinner/Spinner";
import type { AboutInfo } from '../../services';

// Icônes pour réseaux sociaux et informations
import { 
  FaLinkedin, FaGithub, FaDiscord, FaSlack, FaFigma, FaTrello,
  FaWheelchair, FaCar, FaEnvelope, FaPhone, FaMapMarkerAlt
} from 'react-icons/fa';

interface AboutProps {
  forceVisible?: boolean;
}

export const About: React.FC<AboutProps> = ({ 
  forceVisible = false 
}) => {
  const navigate = useNavigate();
  // ================================
  // HOOKS PRINCIPAUX
  // ================================
  
  const { t, language } = useLanguage();
  
  // ================================
  // ÉTAT LOCAL
  // ================================
  
  const [isVisible, setIsVisible] = useState(false);
  const [aboutData, setAboutData] = useState<AboutInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorState, setErrorState] = useState<string | null>(null);
  
  // ✅ CORRECTION - États pour images avec gestion d'erreur améliorée
  const [profileImageUrl, setProfileImageUrl] = useState<string>('');
  const [flagImageUrl, setFlagImageUrl] = useState<string>('');
  const [imagesLoading, setImagesLoading] = useState(true);
  const [imageErrors, setImageErrors] = useState({
    profile: false,
    flag: false
  });
  // Séquence admin (déplacée ici, sans indicateur visuel)
  const [adminClicks, setAdminClicks] = useState(0);
  const [adminTimeoutId, setAdminTimeoutId] = useState<NodeJS.Timeout | null>(null);
  
  // ================================
  // INTERSECTION OBSERVER
  // ================================
  
  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  // ================================
  // CHARGEMENT DES IMAGES DEPUIS SUPABASE
  // ================================
  // CALLBACKS POUR LES APPELS API
  // ================================
  
  const normalizeImageUrl = useCallback((rawUrl?: string): string => {
    return normalizeImageUrlShared(rawUrl);
  }, []);

  const getFallbackProfileImage = useCallback((): string => {
    return '/placeholder-image.svg';
  }, []);

  const loadImages = useCallback(async () => {
    try {
      setImagesLoading(true);

      setImageErrors({ profile: false, flag: false });

      if (aboutData?.profile_image_url) {
        const normalizedProfileUrl = normalizeImageUrl(aboutData.profile_image_url);
        setProfileImageUrl(normalizedProfileUrl || getFallbackProfileImage());
      } else {
        setProfileImageUrl(getFallbackProfileImage());
      }

      const localFlag = language === 'en' ? '/logos/UK.webp' : '/logos/FR.webp';
      setFlagImageUrl(localFlag);
    } catch (error) {
      console.warn('Error loading images:', error);
      setProfileImageUrl(getFallbackProfileImage());
    } finally {
      setImagesLoading(false);
    }
  }, [aboutData?.profile_image_url, getFallbackProfileImage, language, normalizeImageUrl]);

  const loadAboutData = useCallback(async () => {
    try {
      setLoading(true);
      setErrorState(null);
      
      const response = await apiService.get('/about', true, {
        cacheTtlMs: 180000, // 3 minutes cache pour about
        useCache: true
      });
      if (response?.data) {
        setAboutData(response.data as AboutInfo);
      } else {
        setAboutData(null);
      }
    } catch (error: any) {
      setErrorState(`Erreur de chargement: ${error.message}`);
      setAboutData(null);
    } finally {
      setLoading(false);
    }
  }, [language]);

  // ================================
  // CHARGEMENT DES DONNÉES
  // ================================
  
  useEffect(() => {
    loadAboutData();
  }, [language, loadAboutData]);

  useEffect(() => {
    loadImages();
  }, [aboutData?.profile_image_url, language, loadImages]);

  // Nettoyage timeout admin
  useEffect(() => {
    return () => {
      if (adminTimeoutId) clearTimeout(adminTimeoutId);
    }
  }, [adminTimeoutId]);

  // ================================
  // GESTION DE L'ANIMATION
  // ================================
  
  useEffect(() => {
    if (inView || forceVisible) {
      setIsVisible(true);
    }
  }, [inView, forceVisible]);

  // ================================
  // FONCTIONS UTILITAIRES
  // ================================
  
  /**
   * ✅ CORRECTION - Obtient l'image de profil avec priorités et gestion d'erreur
   */
  const getProfileImage = useCallback((): string => {
    // Priorité 1: Image depuis les données About de l'API (uniquement si visible)
    const visibleAbout = (aboutData && (aboutData.is_visible || forceVisible)) ? aboutData : null;
    if (visibleAbout?.profile_image_url) {
      return normalizeImageUrl(visibleAbout.profile_image_url);
    }

    // Priorité 2: Image depuis le service d'assets Supabase
    if (profileImageUrl) {
      return profileImageUrl;
    }

    // Fallback local pour conserver un rendu visible et cliquable
    return getFallbackProfileImage();
  }, [aboutData, forceVisible, getFallbackProfileImage, normalizeImageUrl, profileImageUrl]);

  /**
   * ✅ CORRECTION - Obtient le drapeau avec priorités et gestion d'erreur
   */
  const getFlagImage = useCallback((): string => {
    // Priorité 1: Image depuis Supabase
    if (flagImageUrl) {
      return flagImageUrl;
    }
    
    // Si aucune image n'est disponible, ne rien retourner
    return '';
  }, [flagImageUrl, language]);

  /**
   * Obtient le texte traduit avec système hybride
   */
  const getText = useCallback((key: string): string => {
    const visibleAbout = (aboutData && (aboutData.is_visible || forceVisible)) ? aboutData : null;
    if (visibleAbout) {
      const fieldMap: { [key: string]: keyof AboutInfo } = {
        about_title: `about_title_${language}` as keyof AboutInfo,
        name: `name_${language}` as keyof AboutInfo,
        title: `title_${language}` as keyof AboutInfo,
        bio: `bio_${language}` as keyof AboutInfo,
        about_intro: `about_intro_${language}` as keyof AboutInfo,
        about_career: `about_career_${language}` as keyof AboutInfo,
        about_datascience: `about_datascience_${language}` as keyof AboutInfo,
        about_specialization: `about_specialization_${language}` as keyof AboutInfo,
        about_englishlevel: `about_englishlevel_${language}` as keyof AboutInfo,
        about_rqth: `about_rqth_${language}` as keyof AboutInfo,
        about_permis: `about_permis_${language}` as keyof AboutInfo,
      };
      const field = fieldMap[key];
      if (field && typeof visibleAbout[field] === "string" && (visibleAbout[field] as string).trim().length > 0) {
        return visibleAbout[field] as string;
      }
    }
    // Si aucune donnée n'est disponible, retourner une chaîne vide
    return '';
  }, [aboutData, forceVisible, language, t]);

  /**
   * Fallback statique pour les textes essentiels
   */
  /**
   * Vérifie si un lien social est valide et actif
   */
  const isValidSocialLink = useCallback((url?: string): boolean => {
    return !!(url && url.trim() && url !== '#' && url !== '');
  }, []);

  // ================================
  // GESTIONNAIRES D'ERREUR D'IMAGE (✅ CORRIGÉS)
  // ================================
  
  const handleProfileImageError = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    if (e.currentTarget.currentSrc && !e.currentTarget.currentSrc.endsWith('/placeholder-image.svg')) {
      e.currentTarget.src = getFallbackProfileImage();
    }

    setImageErrors(prev => ({ ...prev, profile: true }));
    setProfileImageUrl(getFallbackProfileImage());
  }, [getFallbackProfileImage]);

  // Clic secret sur l'image de profil: 5 clics en 3s => /admin/login
  const handleProfileImageClick = useCallback(() => {
    const newCount = adminClicks + 1;
    setAdminClicks(newCount);

    if (adminTimeoutId) clearTimeout(adminTimeoutId);

    if (newCount >= 5) {
      setAdminClicks(0);
      navigate('/admin/login');
      return;
    }

    const tid = setTimeout(() => setAdminClicks(0), 3000);
    setAdminTimeoutId(tid);
  }, [adminClicks, adminTimeoutId, navigate]);

  const handleFlagImageError = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    e.currentTarget.style.display = 'none'; // Masquer le drapeau si il ne se charge pas
    setImageErrors(prev => ({ ...prev, flag: true }));
  }, []);

  // ================================
  // VISIBILITÉ: afficher en fallback si masqué
  // ================================

  // ================================
  // STYLES D'ANIMATION
  // ================================
  
  const animationStyle: React.CSSProperties = {
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
    transition: 'opacity 0.6s ease-out, transform 0.6s ease-out'
  };

  // ================================
  // DONNÉES FALLBACK
  // ================================
  
  const fallbackData = {
    name: 'Jean-François CHAUSSOY',
    title: language === 'fr' ? 'Développeur Web Full Stack' : 'Full Stack Web Developer',
    secondaryTitle: language === 'fr' ? 'Data Engineering & IA' : 'Data Engineering & AI',
    socialLinks: {
      linkedin: 'https://www.linkedin.com/in/jfchaussoy-profil',
      github: 'https://github.com/jfchaussoy',
      discord: 'https://discord.com/users/Sethfield#6444',
      slack: 'https://app.slack.com/user/U082DBZE18C'
    }
  };

  // Déterminer si on affiche en mode fallback (pas de données ou données masquées)
  const isFallbackMode = !aboutData || (!aboutData.is_visible && !forceVisible);
  const displayName = (getText('name') || fallbackData.name);
  const displayTitle = (getText('title') || fallbackData.title);
  const displaySecondaryTitle = fallbackData.secondaryTitle;
  const aboutVisible = (aboutData && (aboutData.is_visible || forceVisible)) ? aboutData : null;

  // ================================
  // AFFICHAGE DES ÉTATS DE CHARGEMENT
  // ================================
  
  if (loading || imagesLoading) {
    return (
      <section 
        id="about" 
        className={`${commonClasses.section} ${styles.section}`} 
        ref={ref}
      >
        <div className={`${commonClasses.container} ${styles.container}`}>
          <div className={styles.loadingIndicator}>
            <Spinner size="large" ariaLabel={language === 'fr' ? 'Chargement en cours' : 'Loading'} />
          </div>
        </div>
      </section>
    );
  }

  // ================================
  // RENDU PRINCIPAL
  // ================================
  
  return (
    <section 
      id="about" 
      className={`${commonClasses.section} ${styles.section}`} 
      ref={ref}
      role="main"
      aria-labelledby="about-title"
    >
      <div className={`${commonClasses.container} ${styles.container}`} style={animationStyle}>
        
        {/* ================================ */}
        {/* TITRE DE LA SECTION */}
        {/* ================================ */}
        
        <div className={styles.titleWrapper}>
          <h2 
            id="about-title"
            className={`${commonClasses.title} ${styles.title}`}
          >
            <span className={`${commonClasses.highlight} ${styles.highlight}`}>01.</span> 
            <span data-translate="true">
              {getText('about_title') || (language === 'fr' ? 'À propos' : 'About')}
            </span>
          </h2>
        </div>
        
        {/* ================================ */}
        {/* CONTENU PRINCIPAL */}
        {/* ================================ */}
        
        <div className={styles.content}>
          
          {/* ================================ */}
          {/* PROFIL ET INFORMATIONS */}
          {/* ================================ */}
          
          <div className={styles.profileContainer}>
            {/* ✅ Image de profil DYNAMIQUE avec gestion d'erreur améliorée */}
            <div className={styles.imageContainer}>
              <img
                src={getProfileImage()}
                alt={displayName}
                className={styles.image}
                loading="lazy"
                onError={handleProfileImageError}
                onClick={handleProfileImageClick}
              />
              <div className={styles.imageBorder}></div>
            </div>
            
            {/* Informations de profil */}
            <div className={styles.profileInfo}>
              <h3 className={styles.profileName}>
                {displayName}
              </h3>
              
              <div className={styles.profileDivider}></div>
              
              <div className={styles.profileTitle}>
                <span className={styles.profileMainTitle} data-translate="true">
                  {displayTitle}
                </span>
                <div className={styles.profileJobSeparator}></div>
                <span className={styles.profileSecondaryTitle} data-translate="true">
                  {displaySecondaryTitle}
                </span>
              </div>

              {/* Informations de contact (si disponibles) */}
              {aboutVisible && (aboutVisible.email || aboutVisible.phone || aboutVisible.location) && (
                <div className={styles.contactInfo}>
                  {aboutVisible.email && (
                    <div className={styles.contactItem}>
                      <FaEnvelope className={styles.contactIcon} />
                      <a href={`mailto:${aboutVisible.email}`} className={styles.contactLink}>
                        {aboutVisible.email}
                      </a>
                    </div>
                  )}
                  
                  {aboutVisible.phone && (
                    <div className={styles.contactItem}>
                      <FaPhone className={styles.contactIcon} />
                      <a href={`tel:${aboutVisible.phone}`} className={styles.contactLink}>
                        {aboutVisible.phone}
                      </a>
                    </div>
                  )}
                  
                  {aboutVisible.location && (
                    <div className={styles.contactItem}>
                      <FaMapMarkerAlt className={styles.contactIcon} />
                      <span className={styles.contactText}>{aboutVisible.location}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          
          {/* ================================ */}
          {/* CONTENU TEXTUEL */}
          {/* ================================ */}
          
          <div className={styles.textContent}>
            {/* Biographie en 4 paragraphes dynamiques */}
            <div className={styles.biography}>
              <p className={styles.paragraph} data-translate="true">
                {getText('about_intro')}
              </p>
              
              <p className={styles.paragraph} data-translate="true">
                {getText('about_career')}
              </p>
              
              <p className={styles.paragraph} data-translate="true">
                {getText('about_datascience')}
              </p>
              
              <p className={styles.paragraph} data-translate="true">
                {getText('about_specialization')}
              </p>
            </div>

            {/* ================================ */}
            {/* INFORMATIONS ADDITIONNELLES */}
            {/* ================================ */}
            
            <div className={styles.additionalInfo}>
              {/* ✅ Niveau d'anglais avec drapeau DYNAMIQUE */}
              <div className={styles.infoItem}>
                <span className={styles.iconWrapper}>
                  {!imageErrors.flag && getFlagImage() && (
                    <img 
                      src={getFlagImage()} 
                      alt={`Drapeau ${language === 'en' ? 'britannique' : 'français'}`} 
                      className={styles.flagIcon}
                      onError={handleFlagImageError}
                    />
                  )}
                </span>
                <span data-translate="true">
                  {getText('about_englishlevel')}
                </span>
              </div>
              
              {/* Statut RQTH */}
              <div className={styles.infoItem}>
                <span className={styles.iconWrapper}>
                  <FaWheelchair className={styles.infoIcon} />
                </span>
                <span data-translate="true">
                  {getText('about_rqth')}
                </span>
              </div>
              
              {/* Permis de conduire */}
              <div className={styles.infoItem}>
                <span className={styles.iconWrapper}>
                  <FaCar className={styles.infoIcon} />
                </span>
                <span data-translate="true">
                  {getText('about_permis')}
                </span>
              </div>
            </div>
            
            {/* ================================ */}
            {/* LIENS SOCIAUX */}
            {/* ================================ */}
            
            <div className={styles.socialLinks}>
              {/* LinkedIn */}
        {isValidSocialLink(aboutVisible?.linkedin_url) && (
                <a 
          href={aboutVisible!.linkedin_url} 
                  target="_blank" 
                  rel="noopener noreferrer nofollow external" 
                  className={styles.socialLink} 
                  aria-label="Profil LinkedIn"
                  title="Voir mon profil LinkedIn"
                >
                  <FaLinkedin className={styles.socialIcon} />
                </a>
              )}
              
              {/* GitHub */}
        {isValidSocialLink(aboutVisible?.github_url) && (
                <a 
          href={aboutVisible!.github_url} 
                  target="_blank" 
                  rel="noopener noreferrer nofollow external" 
                  className={styles.socialLink} 
                  aria-label="Profil GitHub"
                  title="Voir mon profil GitHub"
                >
                  <FaGithub className={styles.socialIcon} />
                </a>
              )}
              
              {/* Discord */}
        {isValidSocialLink(aboutVisible?.discord_url) && (
                <a 
          href={aboutVisible!.discord_url} 
                  target="_blank" 
                  rel="noopener noreferrer nofollow external" 
                  className={styles.socialLink} 
                  aria-label="Profil Discord"
                  title="Me contacter sur Discord"
                >
                  <FaDiscord className={styles.socialIcon} />
                </a>
              )}
              
              {/* Slack */}
        {isValidSocialLink(aboutVisible?.slack_url) && (
                <a 
          href={aboutVisible!.slack_url} 
                  target="_blank" 
                  rel="noopener noreferrer nofollow external" 
                  className={styles.socialLink} 
                  aria-label="Profil Slack"
                  title="Me contacter sur Slack"
                >
                  <FaSlack className={styles.socialIcon} />
                </a>
              )}
              
              {/* Figma */}
        {isValidSocialLink(aboutVisible?.figma_url) && (
                <a 
          href={aboutVisible!.figma_url} 
                  target="_blank" 
                  rel="noopener noreferrer nofollow external" 
                  className={styles.socialLink} 
                  aria-label="Profil Figma"
                  title="Voir mes designs Figma"
                >
                  <FaFigma className={styles.socialIcon} />
                </a>
              )}
              
              {/* Trello */}
        {isValidSocialLink(aboutVisible?.trello_url) && (
                <a 
          href={aboutVisible!.trello_url} 
                  target="_blank" 
                  rel="noopener noreferrer nofollow external" 
                  className={styles.socialLink} 
                  aria-label="Profil Trello"
                  title="Voir mes boards Trello"
                >
                  <FaTrello className={styles.socialIcon} />
                </a>
              )}

              {/* Liens fallback si pas de données */}
              {isFallbackMode && (
                <>
                  <a 
                    href={fallbackData.socialLinks.linkedin} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className={styles.socialLink}
                    aria-label="Profil LinkedIn"
                  >
                    <FaLinkedin className={styles.socialIcon} />
                  </a>
                  <a 
                    href={fallbackData.socialLinks.github} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className={styles.socialLink}
                    aria-label="Profil GitHub"
                  >
                    <FaGithub className={styles.socialIcon} />
                  </a>
                  <a 
                    href={fallbackData.socialLinks.discord} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className={styles.socialLink}
                    aria-label="Profil Discord"
                  >
                    <FaDiscord className={styles.socialIcon} />
                  </a>
                </>
              )}
            </div>
          </div>
        </div>

        {/* ================================ */}
        {/* INDICATEURS D'ÉTAT */}
        {/* ================================ */}
        
        {/* Indicateur d'erreur */}
        {errorState && !loading && (
          <div className={styles.errorIndicator}>
            <p className={styles.errorText}>⚠️ {errorState}</p>
            <p className={styles.errorSubtext}>
              {language === 'fr' ? 'Utilisation des données par défaut' : 'Using default data'}
            </p>
          </div>
        )}

        {/* Indicateur de mode fallback */}
  {isFallbackMode && !loading && !errorState && (
          <div className={styles.fallbackIndicator}>
            <p className={styles.fallbackText}>
              {language === 'fr' 
                ? 'ℹ️ Affichage en mode par défaut - Configurez vos informations dans l\'admin'
                : 'ℹ️ Displaying in default mode - Configure your information in admin'
              }
            </p>
          </div>
        )}
      </div>
    </section>
  );
};

export default About;

